// Header card: brand, name, icon-led stat tiles, 7-day calorie strip.
// Name priority: profile.name → phone entered at gate → default string.
// Phone is formatted as (XXX) XXX-XXXX for US 10/11-digit numbers.
// All SVG icons are inline zero-dep paths, white/translucent on the pink header.

import type { DashboardProfile, DashboardTargets, DashboardCaloriePoint } from '../lib/types'
import type { Strings } from '../lib/i18n'

// Calorie status thresholds — mirror the constants in DayLog.tsx.
const CAL_UNDER_RATIO  = 0.9
const CAL_ON_MAX_RATIO = 1.05

interface Props {
  profile: DashboardProfile
  targets: DashboardTargets
  calorieSeries: DashboardCaloriePoint[]
  /** Phone the user typed at the gate — used as name fallback. Never from server. */
  phone: string | null
  strings: Strings
}

function formatPhone(raw: string | null): string | null {
  if (!raw) return null
  const digits = raw.replace(/\D/g, '')
  // US 10-digit or 11-digit (1XXXXXXXXXX)
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (local.length === 10) {
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
  }
  // Non-US or unusual format — return as-is
  return raw
}

// ── Inline SVG icons (white/translucent, 18×18 display, 24×24 viewBox) ───────

function FlameIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2C12 2 7 8 7 13a5 5 0 0010 0c0-3-2-5-2-5s-.5 2-2 2c-1.1 0-2-.9-2-2 0-2 1-4 1-4z"
        fill="rgba(255,255,255,0.9)"
      />
    </svg>
  )
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="17" rx="2" stroke="rgba(255,255,255,0.9)" strokeWidth="2" />
      <path d="M16 2v4M8 2v4M3 9h18" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function TargetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.9)" strokeWidth="2" />
      <circle cx="12" cy="12" r="5" stroke="rgba(255,255,255,0.9)" strokeWidth="2" />
      <circle cx="12" cy="12" r="1.5" fill="rgba(255,255,255,0.9)" />
    </svg>
  )
}

// ── Last-7-days calorie status strip ─────────────────────────────────────────

function Last7Strip({
  calorieSeries,
  dailyCalTarget,
}: {
  calorieSeries: DashboardCaloriePoint[]
  dailyCalTarget: number
}) {
  const today = new Date().toISOString().slice(0, 10)
  const todayMs = new Date(today + 'T00:00:00Z').getTime()

  // Build the 7 days ending today (inclusive), oldest → newest left to right.
  const days: string[] = []
  for (let i = 6; i >= 0; i--) {
    days.push(new Date(todayMs - i * 86_400_000).toISOString().slice(0, 10))
  }

  const calByDate: Record<string, number> = {}
  for (const p of calorieSeries) calByDate[p.date] = p.calories

  return (
    <div className="header-last7">
      {days.map((date) => {
        const cal = calByDate[date]
        let statusCls = 'none'
        if (cal !== undefined) {
          if (dailyCalTarget <= 0) {
            statusCls = 'neutral'
          } else {
            const ratio = cal / dailyCalTarget
            if (ratio < CAL_UNDER_RATIO) statusCls = 'under'
            else if (ratio <= CAL_ON_MAX_RATIO) statusCls = 'on'
            else statusCls = 'over'
          }
        }
        const isToday = date === today
        const cls = [
          'header-last7-dot',
          `header-last7-dot-${statusCls}`,
          isToday ? 'header-last7-dot-today' : '',
        ].filter(Boolean).join(' ')
        return <div key={date} className={cls} />
      })}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function UserHeader({
  profile,
  targets,
  calorieSeries,
  phone,
  strings: s,
}: Props) {
  const name = profile.name?.trim() || formatPhone(phone) || s.headerDefaultName
  return (
    <div className="dash-header">
      {/* Top row: brand (language toggle removed — English only) */}
      <div className="dash-header-top">
        <div className="dash-header-brand">{s.brand}</div>
      </div>

      {/* User name — Lv badge removed */}
      <div className="dash-header-name">{name}</div>

      {/* Icon-led stat tiles: streak · days on plan · cal target */}
      <div className="dash-header-stats">
        <div className="dash-header-stat">
          <div className="dash-header-stat-icon"><FlameIcon /></div>
          <span className="dash-header-stat-value">{profile.streak}</span>
          <span className="dash-header-stat-label">{s.headerStreakLabel}</span>
        </div>
        <div className="dash-header-stat-divider" />
        <div className="dash-header-stat">
          <div className="dash-header-stat-icon"><CalendarIcon /></div>
          <span className="dash-header-stat-value">{profile.days_active}</span>
          <span className="dash-header-stat-label">{s.headerDaysActive}</span>
        </div>
        <div className="dash-header-stat-divider" />
        <div className="dash-header-stat">
          <div className="dash-header-stat-icon"><TargetIcon /></div>
          <span className="dash-header-stat-value">{targets.daily_calories}</span>
          <span className="dash-header-stat-label">{s.headerCalorieTarget}</span>
        </div>
      </div>

      {/* Last-7-days calorie status strip */}
      <div className="header-last7-wrap">
        <span className="header-last7-label">{s.headerLast7Label}</span>
        <Last7Strip calorieSeries={calorieSeries} dailyCalTarget={targets.daily_calories} />
      </div>
    </div>
  )
}
